import type { PipelineType } from '@crm/contracts';
import { useState } from 'react';
import type { PipelineView } from '../../lib/client.ts';
import { stageOptions, usePipelines } from '../../lib/record-data.ts';
import { FieldChip } from '../record-table/quick-filters.tsx';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select.tsx';

const firstOpen = (p: PipelineView | undefined) => p?.stages.find((s) => !s.isTerminal)?.id;

/** Where a new record starts: a pipeline of its type and a stage of it, both unset until loaded. */
export interface StartPick {
  pipelineId: string | undefined;
  stageId: string | undefined;
}

/**
 * The pipeline a new deal or job starts in, and its stage: the type's default pipeline at its first
 * open stage until something is picked. Picking another pipeline moves the stage to its first open
 * one. Returns the choice and the picker to draw (`<StartPicker …>`).
 */
export function useStartPick(type: Extract<PipelineType, 'deal' | 'job'>) {
  const pipelines = usePipelines(type);
  const [pick, setPick] = useState<{ pipelineId: string; stageId: string | undefined } | null>(
    null,
  );
  const pipeline = pipelines.find((p) => p.id === pick?.pipelineId) ?? pipelines[0];
  const value: StartPick = {
    pipelineId: pipeline?.id,
    stageId: pick ? pick.stageId : firstOpen(pipeline),
  };
  return { pipelines, pipeline, value, setPick };
}

/**
 * The Pipeline select (only when the type has more than one) and the lists' stage filter chip,
 * one choice, named after the pipeline.
 */
export function StartPicker({
  pick,
  className,
}: {
  pick: ReturnType<typeof useStartPick>;
  className?: string;
}) {
  const { pipelines, pipeline, value, setPick } = pick;
  return (
    <>
      {pipelines.length > 1 && (
        <Select
          value={pipeline?.id ?? ''}
          onValueChange={(id) =>
            setPick({ pipelineId: id, stageId: firstOpen(pipelines.find((p) => p.id === id)) })
          }
        >
          <SelectTrigger className={className ?? 'w-52'} aria-label="Pipeline">
            <SelectValue placeholder="Pipeline" />
          </SelectTrigger>
          <SelectContent>
            {pipelines.map((p) => (
              <SelectItem key={p.id} value={p.id}>
                {p.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
      {pipeline && (
        <span className="w-60">
          <FieldChip
            key={pipeline.id}
            field={{
              name: 'stageId',
              label: pipeline.name,
              type: 'single_select',
              options: stageOptions(pipeline),
            }}
            conditions={
              value.stageId ? [{ field: 'stageId', op: 'in', value: [value.stageId] }] : []
            }
            onChange={(next) => {
              const c = next.find((x) => x.field === 'stageId');
              const v: unknown = Array.isArray(c?.value) ? c.value[0] : c?.value;
              setPick({ pipelineId: pipeline.id, stageId: typeof v === 'string' ? v : undefined });
            }}
            single
            block
          />
        </span>
      )}
    </>
  );
}
